import { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowLeft, Camera, ClipboardPaste, Image as Galeria, MoreVertical, Send, Trash2, X } from 'lucide-react';
import { equipeRespondeSemSerDona } from '@fiodecobre/shared';
import { useAuth } from '../../lib/auth';
import { atendimentoApi } from '../atendimento/lib/api';
import type { Conversa, Mensagem } from '../atendimento/lib/tipos';
import { PlayerAudio } from '../app-cliente/PlayerAudio';
import { AnexoDocumento } from '../app-cliente/AnexoDocumento';
import { GravadorVoz } from '../app-cliente/GravadorVoz';
import { fotoDoColar, fotoDoClipboard, ErroClipboard } from '../app-cliente/colar-foto';
import { MenuLoja } from './MenuLoja';

// A conversa aberta dentro do App do Atendente.
//
// ⚠️ **Reusa `PlayerAudio` e `GravadorVoz` do app do cliente de proposito** — sao
// pecas de celular, ja provadas em campo nos dois sistemas, e o player carrega o
// conserto do audio sem duracao (`webm` do `MediaRecorder` nasce sem cabecalho, e o
// ERP ainda mostra `0:00 / 0:00` por causa disso). Copiar o desenho pra ca criaria a
// terceira copia do mesmo problema.
//
// ⚠️ **Le por consulta repetida** (ver o cabecalho do `Atender.tsx`). A leitura e'
// incremental por `after=<ultimo id>`, e a cada N ciclos ela relê o rodape inteiro e
// MESCLA por id — sem isso, mudanca em mensagem JA desenhada (recibo de entrega,
// reacao, edicao) nunca chegaria, que e' o contrato quebrado da [DA-522] p.6.

const COBRE = '#b45309';
const INTERVALO_ATIVO = 4000;
const INTERVALO_OCIOSO = 20000;
const CICLOS_ATE_RELER = 5;
const RELEITURA_LIMITE = 40;

const FOTO_LADO_MAX = 1600;
const FOTO_QUALIDADE = 0.85;

interface Props {
  conversa: Conversa;
  aoVoltar: () => void;
}

async function reduzirFoto(arquivo: File): Promise<{ base64: string; mime: string }> {
  const bitmap = await createImageBitmap(arquivo);
  const escala = Math.min(1, FOTO_LADO_MAX / Math.max(bitmap.width, bitmap.height));
  const largura = Math.round(bitmap.width * escala);
  const altura = Math.round(bitmap.height * escala);
  const canvas = document.createElement('canvas');
  canvas.width = largura;
  canvas.height = altura;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Nao consegui preparar a foto.');
  ctx.drawImage(bitmap, 0, 0, largura, altura);
  bitmap.close?.();
  return { base64: canvas.toDataURL('image/jpeg', FOTO_QUALIDADE).split(',')[1] ?? '', mime: 'image/jpeg' };
}

function horaCurta(iso: string): string {
  return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

function diaLegivel(iso: string): string {
  const d = new Date(iso);
  const hoje = new Date();
  const ontem = new Date(hoje);
  ontem.setDate(hoje.getDate() - 1);
  const mesmoDia = (a: Date, b: Date) => a.toDateString() === b.toDateString();
  if (mesmoDia(d, hoje)) return 'Hoje';
  if (mesmoDia(d, ontem)) return 'Ontem';
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long' });
}

export function ChatAtender({ conversa, aoVoltar }: Props) {
  const { usuario, logout } = useAuth();
  const [mensagens, setMensagens] = useState<Mensagem[]>([]);
  const [texto, setTexto] = useState('');
  const [carregando, setCarregando] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [menuFoto, setMenuFoto] = useState(false);
  /** Mensagem com a folha de opcoes aberta (apagar). */
  const [menuMsgId, setMenuMsgId] = useState<number | null>(null);
  const [apagando, setApagando] = useState(false);
  const [fotoAberta, setFotoAberta] = useState<string | null>(null);
  const fotoInputRef = useRef<HTMLInputElement | null>(null);
  const cameraInputRef = useRef<HTMLInputElement | null>(null);
  const rolagemRef = useRef<HTMLDivElement | null>(null);
  // Enquanto verdadeiro, a tela ACOMPANHA o rodape. Vira falso quando quem atende
  // sobe pra ler — subir e' um pedido de ficar parado ali.
  const seguirFimRef = useRef(true);
  // Ultimo topo que NOS escrevemos — e' o que separa a rolagem de quem atende da nossa.
  const topoPostoRef = useRef(-1);
  const ultimoIdRef = useRef<number>(0);

  // Quem pode responder: a mesma regra do servidor, e agora literalmente a MESMA PECA —
  // `equipeRespondeSemSerDona` do `@fiodecobre/shared`, que o `checarPermissaoConversa`
  // tambem le ([DA-620]). Antes a lista de caixas livres era escrita a mao aqui, no
  // `PainelChat` e no servidor: tres copias, tres lugares pra divergir, e com a condicao
  // errada o campo de digitar abre e o envio volta 403 na cara de quem clicou ([DA-539]).
  // Desde 26/08/2026 a caixa `app` esta nessa lista — qualquer conta da loja responde
  // qualquer conversa do AppCel, sem tomar posse.
  // O celular do balcao segue respondendo qualquer conversa do AppCel (15/08/2026): e'
  // aparelho compartilhado, e o campo `quiosque` vem do `/me`, nunca de lista de nome
  // escrita no front, que foi o erro revertido na [DA-543].
  const souDono = conversa.usuario_atribuido_id === usuario?.id;
  const mandaQuemQuiser = equipeRespondeSemSerDona(conversa.inbox) || ['admin', 'gerente'].includes(usuario?.perfil ?? '');
  const podeResponder = mandaQuemQuiser || souDono || !!usuario?.quiosque;

  const descer = useCallback((suave: boolean) => {
    const el = rolagemRef.current;
    if (!el) return;
    seguirFimRef.current = true;
    el.scrollTo({ top: el.scrollHeight, behavior: suave ? 'smooth' : 'auto' });
    if (!suave) topoPostoRef.current = el.scrollTop;
  }, []);

  // ⚠️ **A foto so ocupa espaco DEPOIS de carregar, e ate la o fim da conversa e'
  // mentira** — mesmo defeito e mesma cura do chat do cliente (`ChatApp.tsx`), onde
  // a medicao esta escrita por extenso. Aqui vale igual porque e' a mesma conversa
  // vista do outro lado: quem manda orcamento manda JPG comprido, e uma conversa
  // de 13 fotos abriu a 11% do caminho.
  const aoCarregarFoto = useCallback(() => {
    if (seguirFimRef.current) descer(false);
  }, [descer]);

  // ⚠️ O evento de rolagem chega DEPOIS, e a conversa pode ter crescido no meio —
  // "esta longe do fim" nao prova que alguem subiu a tela. Medicao e' a mesma do
  // `ChatApp.tsx`, onde ela esta escrita por extenso.
  const aoRolar = useCallback(() => {
    const el = rolagemRef.current;
    if (!el) return;
    if (el.scrollTop === topoPostoRef.current) return;
    seguirFimRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  }, []);

  const juntar = useCallback((novas: Mensagem[]) => {
    if (!novas.length) return;
    setMensagens((atuais) => {
      const porId = new Map(atuais.map((m) => [m.id, m] as const));
      let mudou = false;
      for (const nova of novas) {
        const antiga = porId.get(nova.id);
        if (!antiga) { porId.set(nova.id, nova); mudou = true; continue; }
        // Campos que MUDAM depois de a mensagem ja estar na tela. Campo mutavel novo
        // nesta entidade obriga a mexer nesta linha — senao a tela recebe o valor
        // certo a cada releitura e o descarta calada.
        if (
          antiga.status !== nova.status ||
          antiga.texto_editado !== nova.texto_editado ||
          JSON.stringify(antiga.metadata?.reactions ?? null) !== JSON.stringify(nova.metadata?.reactions ?? null)
        ) {
          porId.set(nova.id, { ...antiga, status: nova.status, texto_editado: nova.texto_editado, metadata: nova.metadata });
          mudou = true;
        }
      }
      if (!mudou) return atuais;
      return [...porId.values()].sort((a, b) => a.id - b.id);
    });
    ultimoIdRef.current = Math.max(ultimoIdRef.current, ...novas.map((m) => m.id));
  }, []);

  useEffect(() => {
    let vivo = true;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let ciclo = 0;
    ultimoIdRef.current = 0;
    setMensagens([]);
    setCarregando(true);

    async function buscar(primeira: boolean) {
      ciclo += 1;
      const releitura = !primeira && ciclo % CICLOS_ATE_RELER === 0;
      try {
        const anterior = ultimoIdRef.current;
        const lista = await atendimentoApi.listarMensagens(
          conversa.id,
          primeira || releitura ? { limit: releitura ? RELEITURA_LIMITE : 60 } : { after: anterior || undefined },
        );
        if (!vivo) return;
        juntar(lista);
        const chegouNova = lista.some((m) => m.id > anterior);
        if (primeira) {
          setCarregando(false);
          requestAnimationFrame(() => descer(false));
        } else if (chegouNova) {
          requestAnimationFrame(() => descer(true));
        }
      } catch {
        if (!vivo) return;
        if (primeira) setCarregando(false);
      }
      if (!vivo) return;
      timer = setTimeout(() => void buscar(false), document.hidden ? INTERVALO_OCIOSO : INTERVALO_ATIVO);
    }

    function aoVoltarPraTela() {
      if (document.hidden || !vivo) return;
      if (timer) clearTimeout(timer);
      void buscar(false);
    }
    document.addEventListener('visibilitychange', aoVoltarPraTela);

    void buscar(true);
    return () => {
      vivo = false;
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', aoVoltarPraTela);
    };
  }, [conversa.id, juntar, descer]);

  async function enviarTexto() {
    const t = texto.trim();
    if (!t || enviando) return;
    setEnviando(true);
    setErro(null);
    try {
      const criada = await atendimentoApi.enviarTexto(conversa.id, { texto: t });
      setTexto('');
      juntar([criada]);
      requestAnimationFrame(() => descer(true));
    } catch (e) {
      setErro((e as Error).message || 'Nao consegui enviar.');
    } finally {
      setEnviando(false);
    }
  }

  async function enviarAudio(blob: Blob, mime: string) {
    setEnviando(true);
    setErro(null);
    try {
      const base64 = await new Promise<string>((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(String(fr.result).split(',')[1] ?? '');
        fr.onerror = () => reject(new Error('falha ao ler o audio'));
        fr.readAsDataURL(blob);
      });
      const criada = await atendimentoApi.enviarAudio(conversa.id, { base64, mime });
      juntar([criada]);
      requestAnimationFrame(() => descer(true));
    } catch (e) {
      setErro((e as Error).message || 'Nao consegui enviar o audio.');
    } finally {
      setEnviando(false);
    }
  }

  async function enviarFoto(arquivo: File) {
    setEnviando(true);
    setErro(null);
    try {
      const { base64, mime } = await reduzirFoto(arquivo);
      const criada = await atendimentoApi.enviarMidia(conversa.id, {
        tipo: 'image', base64, mime, nome: 'foto.jpg',
      });
      juntar([criada]);
      requestAnimationFrame(() => descer(true));
    } catch (e) {
      setErro((e as Error).message || 'Nao consegui enviar a foto.');
    } finally {
      setEnviando(false);
    }
  }

  // Colar print pelo BOTAO — no iPhone o evento de colar no campo de texto nao
  // entrega arquivo, entao sem isto o recurso valeria so pra metade da loja.
  async function colarPrint() {
    setMenuFoto(false);
    try {
      const arq = await fotoDoClipboard();
      if (!arq) {
        setErro('Nao achei imagem copiada. Tire o print, toque em Copiar e volte aqui.');
        return;
      }
      await enviarFoto(arq);
    } catch (e) {
      // O codigo entre parenteses e' a sonda do caso do iPhone — ver `colar-foto`.
      const cod = e instanceof ErroClipboard ? ` (${e.codigo})` : '';
      setErro(`Nao consegui ler o que foi copiado${cod}. Use "Escolher da galeria" — o print fica salvo la.`);
    }
  }

  async function apagarParaTodos(mensagemId: number) {
    setApagando(true);
    setErro(null);
    try {
      await atendimentoApi.excluirMensagem(mensagemId, { paraTodos: true });
      // Some da tela sem deixar marca — e' o que a proxima consulta tambem vai
      // devolver, porque o servidor passou a recusar as apagadas pra loja.
      setMensagens((atuais) => atuais.filter((m) => m.id !== mensagemId));
      setMenuMsgId(null);
    } catch (e) {
      setErro((e as Error).message || 'Nao consegui apagar.');
    } finally {
      setApagando(false);
    }
  }

  const contatoNome = conversa.contato.nome || conversa.contato.push_name || conversa.contato.telefone || '';
  const canal = conversa.inbox === 'app' ? 'Aplicativo' : conversa.inbox === 'compras' ? 'Compras' : 'WhatsApp';
  let diaAnterior = '';

  return (
    <div
      className="app-atender-root"
      style={{ height: '100dvh', display: 'flex', flexDirection: 'column', backgroundColor: '#faf8f6', color: '#111' }}
    >
      <header style={{ backgroundColor: COBRE, color: '#fff', padding: '9px 12px 9px 4px', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        <button
          type="button"
          onClick={aoVoltar}
          aria-label="Voltar para a lista"
          style={{ width: 42, height: 42, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', flexShrink: 0 }}
        >
          <ArrowLeft size={21} />
        </button>
        {/* O ☰ tambem vive AQUI, dentro da conversa — pedido dele em 16/08/2026, depois
            de abrir o AppCel e nao achar o menu.
            ⚠️ Ele fica na ESQUERDA, colado na seta, e nao no canto direito: o mesmo
            aplicativo nao pode mudar de gramatica conforme a tela, e na lista o ☰ ja
            mora nesse canto. Um slot ao lado o dedo reencontra; do outro lado da tela,
            nao. A seta continua sendo o primeiro item porque sair da conversa e' o
            gesto mais frequente. O nome do contato segue no `flex: 1` com reticencias,
            que e' o que absorve os 42px a menos. */}
        <MenuLoja nome={usuario?.nome ?? ''} perfil={usuario?.perfil ?? ''} aoSair={() => void logout()} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {contatoNome}
          </div>
          <div style={{ fontSize: 11, opacity: 0.9, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {canal}
            {(conversa.usuario_atribuido_login || conversa.usuario_atribuido_nome) ? ` · ${(conversa.usuario_atribuido_login || conversa.usuario_atribuido_nome)!.split(/\s+/)[0]} atende` : ''}
          </div>
        </div>
      </header>

      <div ref={rolagemRef} onScroll={aoRolar} style={{ flex: 1, overflowY: 'auto', padding: '13px 12px 8px' }}>
        {carregando ? (
          <p style={{ textAlign: 'center', color: '#6b7280', fontSize: 14, marginTop: 24 }}>Carregando...</p>
        ) : mensagens.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#6b7280', fontSize: 14, marginTop: 30, padding: '0 26px', lineHeight: 1.6 }}>
            <p style={{ fontWeight: 600, color: '#374151' }}>Nenhuma mensagem ainda.</p>
            <p style={{ marginTop: 8 }}>
              {conversa.inbox === 'app'
                ? `${contatoNome} se cadastrou no aplicativo. Escreva o primeiro alo.`
                : 'Escreva a primeira mensagem.'}
            </p>
          </div>
        ) : (
          mensagens.map((m) => {
            const dia = diaLegivel(m.criado_em);
            const mostrarDia = dia !== diaAnterior;
            diaAnterior = dia;
            // `outbound` e' da LOJA — vai na direita, como no WhatsApp de quem atende.
            const meu = m.direcao === 'outbound';
            const corpo = m.texto_editado ?? m.texto;
            return (
              <div key={m.id}>
                {mostrarDia && (
                  <div style={{ textAlign: 'center', margin: '14px 0 10px' }}>
                    <span style={{ fontSize: 12, color: '#6b7280', backgroundColor: '#e7e5e4', padding: '3px 10px', borderRadius: 999 }}>{dia}</span>
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: meu ? 'flex-end' : 'flex-start', marginBottom: 8 }}>
                  {/* ⚠️ Botao SEMPRE visivel, nunca "segure o dedo": gesto escondido nao
                      existe pra quem nunca soube dele — mesma regra do botao de reagir
                      no app do cliente. So nas NOSSAS mensagens e so enquanto ha o que
                      apagar. */}
                  {meu && podeResponder && (
                    <button
                      type="button"
                      onClick={() => setMenuMsgId(m.id)}
                      aria-label="Opcoes da mensagem"
                      style={{
                        width: 30, height: 30, flexShrink: 0, borderRadius: 999,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#a8a29e', backgroundColor: 'transparent',
                      }}
                    >
                      <MoreVertical size={17} />
                    </button>
                  )}
                  <div
                    style={{
                      maxWidth: '82%',
                      padding: m.tipo === 'image' ? 4 : '9px 12px',
                      borderRadius: 14,
                      backgroundColor: meu ? '#fde8c8' : '#fff',
                      border: meu ? 'none' : '1px solid #e7e5e4',
                      fontSize: 15,
                      lineHeight: 1.45,
                      wordBreak: 'break-word',
                    }}
                  >
                    {meu && m.assinatura && (
                      <span style={{ display: 'block', fontSize: 10.5, fontWeight: 700, color: '#9a3412', marginBottom: 2 }}>
                        {m.assinatura}
                      </span>
                    )}
                    {m.tipo === 'audio' && m.midia_url ? (
                      <PlayerAudio src={m.midia_url} cor={COBRE} />
                    ) : m.tipo === 'image' && m.midia_url ? (
                      // Legenda ABAIXO da foto, igual ao ramo do documento adiante e ao
                      // `ChatApp` do cliente. Ate 26/08/2026 os dois lados desenhavam so
                      // a `<img>` e a legenda sumia calada ([DA-621]) — inclusive a do
                      // Pix, onde ela carrega a chave.
                      <>
                      <img
                        src={m.midia_url}
                        alt="Foto"
                        // Proporcao que RESERVA o espaco antes da foto chegar — sem
                        // ela o balao tem 8px e a conversa vira uma coluna vazia.
                        // Detalhe por extenso no `ChatApp.tsx`.
                        width={1200}
                        height={1600}
                        loading="lazy"
                        onLoad={aoCarregarFoto}
                        onClick={() => setFotoAberta(m.midia_url)}
                        // Fundo visivel so enquanto a foto nao chegou — lugar reservado
                        // com cara de foto, em vez de buraco branco.
                        style={{ display: 'block', maxWidth: '100%', width: 230, height: 'auto', borderRadius: 11, cursor: 'zoom-in', backgroundColor: '#ece9e6' }}
                      />
                      {m.texto ? (
                        <span style={{ display: 'block', whiteSpace: 'pre-wrap', marginTop: 6, padding: '0 4px 2px', userSelect: 'text' }}>{m.texto}</span>
                      ) : null}
                      </>
                    ) : m.tipo === 'document' && m.midia_url ? (
                      // ⚠️ **Este ramo vem ANTES do `corpo`, e nao e' detalhe de ordem.**
                      // Documento com legenda tem `texto` preenchido: caindo no ramo de
                      // baixo, a tela mostraria so a frase da vendedora e o arquivo
                      // sumiria — pior que o `[document] — abra no sistema` de antes,
                      // porque nem denunciaria que ha anexo ([DA-572]).
                      <>
                        <AnexoDocumento url={m.midia_url} nome={m.midia_nome} bytes={m.midia_bytes} meu={meu} />
                        {corpo ? <span style={{ display: 'block', whiteSpace: 'pre-wrap', marginTop: 6 }}>{corpo}</span> : null}
                      </>
                    ) : corpo ? (
                      <span style={{ whiteSpace: 'pre-wrap' }}>{corpo}</span>
                    ) : (
                      // ⚠️ Tipo que esta tela nao desenha (video, documento, contato,
                      // localizacao) NAO vira balao vazio: ele diz o que e', e o
                      // caminho pra ver continua sendo o ERP. Balao mudo faria a
                      // conversa parecer ter buraco.
                      <span style={{ color: '#78716c', fontStyle: 'italic' }}>
                        [{m.tipo}] — abra no sistema para ver
                      </span>
                    )}
                    <div style={{ fontSize: 11, color: '#78716c', textAlign: 'right', marginTop: 3, paddingRight: m.tipo === 'image' ? 4 : 0 }}>
                      {m.editada ? 'editada · ' : ''}{horaCurta(m.criado_em)}
                      {meu && m.status === 'read' ? ' ✓✓' : meu && (m.status === 'delivered' || m.status === 'sent') ? ' ✓' : ''}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {erro && (
        <div style={{ backgroundColor: '#fef2f2', color: '#b91c1c', padding: '8px 14px', fontSize: 13.5, flexShrink: 0 }}>{erro}</div>
      )}

      {!podeResponder ? (
        <div style={{ backgroundColor: '#fdf0e6', color: '#7c2d12', padding: '12px 14px', fontSize: 13.5, lineHeight: 1.5, borderTop: '1px solid #f3ded0', flexShrink: 0 }}>
          Conversa atribuida a <b>{conversa.usuario_atribuido_login || conversa.usuario_atribuido_nome || 'outra pessoa'}</b> — por isso
          quem responde e' ela.
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, padding: 10, paddingBottom: 'calc(10px + env(safe-area-inset-bottom))', backgroundColor: '#fff', borderTop: '1px solid #e7e5e4', flexShrink: 0 }}>
          <textarea
            className="no-upper"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && !('ontouchstart' in window)) {
                e.preventDefault();
                void enviarTexto();
              }
            }}
            // Colar print — mesma peca do lado do cliente. Vai direto, igual
            // camera e galeria; se sair errado, o "apagar para todos" da [DA-541]
            // e' a volta que este lado tem.
            onPaste={(e) => {
              if (enviando) return;
              const arq = fotoDoColar(e);
              if (arq) void enviarFoto(arq);
            }}
            rows={1}
            placeholder="Responder ao cliente..."
            autoCapitalize="sentences"
            autoCorrect="on"
            spellCheck
            style={{
              flex: 1, resize: 'none', maxHeight: 120, minHeight: 44,
              padding: '11px 14px',
              // 16px nao e' estetica: abaixo disso o Safari do iPhone DA ZOOM no campo
              // ao focar, e a tela inteira fica torta depois.
              fontSize: 16,
              border: '1px solid #d1d5db', borderRadius: 22, backgroundColor: '#fff', color: '#111',
            }}
          />
          {texto.trim() ? (
            <button
              type="button"
              onClick={() => void enviarTexto()}
              disabled={enviando}
              aria-label="Enviar"
              style={{ width: 44, height: 44, borderRadius: 999, backgroundColor: COBRE, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
            >
              <Send size={19} />
            </button>
          ) : (
            <>
              {/* DOIS inputs, como no app do cliente: sem `capture` o Android vai
                  direto pra galeria e nunca oferece a camera; com `capture` a galeria
                  some. A escolha vira nossa, igual nos dois aparelhos. */}
              <input
                ref={fotoInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const arq = e.target.files?.[0];
                  e.target.value = '';
                  if (arq) void enviarFoto(arq);
                }}
              />
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const arq = e.target.files?.[0];
                  e.target.value = '';
                  if (arq) void enviarFoto(arq);
                }}
              />
              <button
                type="button"
                onClick={() => setMenuFoto(true)}
                disabled={enviando}
                aria-label="Enviar foto"
                style={{ width: 44, height: 44, borderRadius: 999, color: COBRE, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
              >
                <Camera size={21} />
              </button>
              <GravadorVoz onGravado={(b, m) => void enviarAudio(b, m)} desabilitado={enviando} />
            </>
          )}
        </div>
      )}

      {menuMsgId !== null && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 60, backgroundColor: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'flex-end' }}>
          {/* Nao fecha por toque no fundo — convencao da casa. Sai pelo Cancelar. */}
          <div
            style={{
              width: '100%', backgroundColor: '#fff', borderTopLeftRadius: 18, borderTopRightRadius: 18,
              padding: 10, paddingBottom: 'calc(10px + env(safe-area-inset-bottom))',
              display: 'flex', flexDirection: 'column', gap: 4,
            }}
          >
            {/* ⚠️ A consequencia vai ESCRITA, e nao so o nome da acao: isto nao tem
                desfazer e some do celular do cliente tambem. Rotulo que promete menos
                do que executa e' o defeito que a casa ja pagou caro. */}
            {/* ⚠️ Dizia "fica marcada como apagada nos dois lados" — falso desde a
                [DA-541], que tirou a lapide da tela justamente porque ele pediu "sem
                rastro nas duas pontas". A frase sobreviveu a mudanca de comportamento
                e ficou prometendo uma marca que nao existe. */}
            <p style={{ padding: '12px 14px 4px', fontSize: 13.5, color: '#6b7280', lineHeight: 1.5, margin: 0 }}>
              A mensagem some para você e para o cliente, sem deixar marca nenhuma nos
              dois lados. Não tem como voltar atrás.
            </p>
            <button
              type="button"
              disabled={apagando}
              onClick={() => void apagarParaTodos(menuMsgId)}
              style={{
                display: 'flex', alignItems: 'center', gap: 14, padding: '15px 14px',
                fontSize: 16, fontWeight: 700, color: '#b91c1c', borderRadius: 12,
              }}
            >
              <Trash2 size={22} /> {apagando ? 'Apagando...' : 'Apagar para todos'}
            </button>
            <button
              type="button"
              onClick={() => setMenuMsgId(null)}
              style={{ padding: '14px', fontSize: 16, color: '#6b7280', borderTop: '1px solid #e5e7eb', marginTop: 4 }}
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {menuFoto && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 60, backgroundColor: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'flex-end' }}>
          <div
            style={{
              width: '100%', backgroundColor: '#fff', borderTopLeftRadius: 18, borderTopRightRadius: 18,
              padding: 10, paddingBottom: 'calc(10px + env(safe-area-inset-bottom))',
              display: 'flex', flexDirection: 'column', gap: 4,
            }}
          >
            <button
              type="button"
              onClick={() => { setMenuFoto(false); cameraInputRef.current?.click(); }}
              style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '15px 14px', fontSize: 16, color: '#111', borderRadius: 12 }}
            >
              <Camera size={22} color={COBRE} /> Tirar foto
            </button>
            <button
              type="button"
              onClick={() => { setMenuFoto(false); fotoInputRef.current?.click(); }}
              style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '15px 14px', fontSize: 16, color: '#111', borderRadius: 12 }}
            >
              <Galeria size={22} color={COBRE} /> Escolher da galeria
            </button>
            <button
              type="button"
              onClick={() => void colarPrint()}
              style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '15px 14px', fontSize: 16, color: '#111', borderRadius: 12 }}
            >
              <ClipboardPaste size={22} color={COBRE} /> Colar print
            </button>
            <button
              type="button"
              onClick={() => setMenuFoto(false)}
              style={{ padding: '14px 14px', fontSize: 16, color: '#6b7280', borderTop: '1px solid #e5e7eb', marginTop: 4 }}
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {fotoAberta && (
        <div
          onClick={() => setFotoAberta(null)}
          style={{ position: 'fixed', inset: 0, zIndex: 50, backgroundColor: 'rgba(0,0,0,0.92)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}
        >
          <img src={fotoAberta} alt="Foto" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
          <button
            type="button"
            onClick={() => setFotoAberta(null)}
            aria-label="Fechar"
            style={{
              position: 'absolute', top: 14, right: 14, width: 44, height: 44, borderRadius: 999,
              backgroundColor: 'rgba(255,255,255,0.16)', color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <X size={22} />
          </button>
        </div>
      )}
    </div>
  );
}
